import { useCallback } from "react";

import {
  BuyHoldingPayload,
  DividendPayload,
  EditTradeCommandPayload,
  HoldingFeePayload,
  SellHoldingPayload,
  TradeCommand,
  useBuyHoldingMutation,
  useEditTradeCommandMutation,
  useRecordHoldingDividendMutation,
  useRecordHoldingFeeMutation,
  useRecordWithholdingTaxMutation,
  useSellHoldingMutation,
  WithholdingTaxPayload,
} from "../../../../services";
import { capitalizeFirstLetter, formatDate } from "../../../../utils";
import { commandNoun, isSettlementType } from "../../utils";
import {
  acceptsCashback,
  acceptsCharge,
  acceptsDirection,
  cashbackFundingPayloadOf,
  chargeFieldOf,
  HoldingFormValues,
  HoldingSubmitHelpers,
  HoldingTradeFormValues,
  HoldingTradeSubmitHelpers,
  mapHoldingTradeActionError,
  mapHoldingTradeError,
  mapTradeCorrectionError,
  TradeCorrectionFormValues,
  TradeCorrectionSubmitHelpers,
} from "../../panels";
import { normalizeQuantity } from "../../panels/tradeValidation";
import { noteFields } from "./noteFields";
import { submitLedgerCommand } from "./submitLedgerCommand";
import { UseHoldingCommandsProps, UseHoldingCommandsReturn } from "./types";

// @Min(0.01) rejects zero, so a blank field omits the key — which is how the charge is removed. Same predicate the panel gates the field on.
const correctionChargeOf = (
  command: TradeCommand,
  values: TradeCorrectionFormValues,
): Pick<EditTradeCommandPayload, "feeEur" | "taxWithheld"> => {
  const field = chargeFieldOf(command);
  if (!acceptsCharge(command, values.direction) || values[field] === "") {
    return {};
  }
  const charge = Number(values[field]);
  return field === "feeEur" ? { feeEur: charge } : { taxWithheld: charge };
};

// The three ledger commands that name a position. None refetches by hand —
// `holdingTradeRipple` invalidates every cache the write moves.
export const useHoldingCommands = ({
  investmentId,
  accountId,
  onBuyRecorded,
  onTradeRecorded,
  correctedCommand,
  onCorrectionRecorded,
  settlementAccountName,
}: UseHoldingCommandsProps): UseHoldingCommandsReturn => {
  const isLinkedSettlement = settlementAccountName !== null;
  const [buyHoldingTrigger] = useBuyHoldingMutation();
  const [sellHoldingTrigger] = useSellHoldingMutation();
  const [recordDividendTrigger] = useRecordHoldingDividendMutation();
  const [recordFeeTrigger] = useRecordHoldingFeeMutation();
  const [recordWithholdingTaxTrigger] = useRecordWithholdingTaxMutation();
  const [editTradeCommandTrigger] = useEditTradeCommandMutation();

  // A buy is the only way to create a holding — one call writes cash events, debits broker cash, and patches the investment. The panel's setError/banner setter are passed down so a rejection routes onto the right field.
  const buyHolding = useCallback(
    async (values: HoldingFormValues, helpers: HoldingSubmitHelpers) => {
      const { instrument } = values;
      // The panel's `required` rule already gates this, but that guarantee lives
      // on the other side of a file boundary — narrow here instead of asserting.
      if (!instrument) {
        helpers.setPanelError("Pick an instrument before buying.");
        return;
      }

      const payload: BuyHoldingPayload = {
        accountId,
        instrumentId: instrument.instrumentId,
        // Stays a string — a JSON number cannot carry DECIMAL(28,6) and the DTO
        // rejects one outright.
        quantity: normalizeQuantity(values.quantity),
        amountEur: Number(values.amountEur),
        // `@Min(0.01)` rejects a zero fee, so a blank field omits the key entirely.
        ...(values.feeEur === "" ? {} : { feeEur: Number(values.feeEur) }),
        ...cashbackFundingPayloadOf(values),
        effectiveDate: formatDate(values.date),
        ...noteFields(values.note),
      };

      await submitLedgerCommand<HoldingFormValues>({
        promise: buyHoldingTrigger({
          investmentId,
          payload,
          movesTransactions:
            isLinkedSettlement || payload.cashbackEur !== undefined,
        }).unwrap(),
        successMessage: "Holding Added",
        onRecorded: onBuyRecorded,
        helpers,
        mapError: mapHoldingTradeError,
        settlementAccountName,
      });
    },
    [
      accountId,
      investmentId,
      buyHoldingTrigger,
      onBuyRecorded,
      isLinkedSettlement,
      settlementAccountName,
    ],
  );

  // Branched here, not in the panel: the four payloads are genuinely different shapes — forbidNonWhitelisted rejects a dividend carrying `quantity` as a 400 — so each arm builds exactly its own DTO.
  const recordHoldingTrade = useCallback(
    async (
      values: HoldingTradeFormValues,
      helpers: HoldingTradeSubmitHelpers,
    ) => {
      const { action, holding } = values;
      // The panel's `required` rule already gates this, but that guarantee lives
      // on the other side of a file boundary — narrow here instead of asserting.
      if (!holding) {
        helpers.setPanelError("Pick a position before recording.");
        return;
      }

      const shared = {
        accountId,
        holdingId: holding.holdingId,
        amountEur: Number(values.amountEur),
        effectiveDate: formatDate(values.date),
        ...noteFields(values.note),
      };

      const record = () => {
        if (action === "sell") {
          const payload: SellHoldingPayload = {
            ...shared,
            // Stays a string — a JSON number cannot carry DECIMAL(28,6) and the
            // DTO rejects one outright.
            quantity: normalizeQuantity(values.quantity),
            // `@Min(0.01)` rejects a zero fee, so a blank field omits the key
            // entirely. Sell-only: the other three DTOs whitelist it away.
            ...(values.feeEur !== "" ? { feeEur: Number(values.feeEur) } : {}),
          };
          return {
            promise: sellHoldingTrigger({
              investmentId,
              payload,
              movesTransactions: isLinkedSettlement,
            }).unwrap(),
            successMessage: "Sale recorded",
          };
        }
        if (action === "dividend") {
          const dividendPayload: DividendPayload = {
            ...shared,
            // Tax withheld at source, second leg of the same command; same @Min(0.01) rule as the sell's fee, so a blank field omits the key.
            ...(values.taxWithheld !== ""
              ? { taxWithheld: Number(values.taxWithheld) }
              : {}),
          };
          return {
            promise: recordDividendTrigger({
              investmentId,
              payload: dividendPayload,
              movesTransactions: isLinkedSettlement,
            }).unwrap(),
            successMessage: "Dividend recorded",
          };
        }
        if (action === "tax") {
          // Same four keys, its own DTO server-side — so its own payload type
          // here rather than passing `shared` through under a borrowed name.
          const taxPayload: WithholdingTaxPayload = shared;
          return {
            promise: recordWithholdingTaxTrigger({
              investmentId,
              payload: taxPayload,
              movesTransactions: isLinkedSettlement,
            }).unwrap(),
            successMessage: "Withholding tax recorded",
          };
        }
        const feePayload: HoldingFeePayload = shared;
        return {
          promise: recordFeeTrigger({
            investmentId,
            payload: feePayload,
            movesTransactions: isLinkedSettlement,
          }).unwrap(),
          successMessage: "Fee recorded",
        };
      };

      await submitLedgerCommand<HoldingTradeFormValues>({
        ...record(),
        onRecorded: onTradeRecorded,
        helpers,
        mapError: mapHoldingTradeActionError,
        settlementAccountName,
      });
    },
    [
      accountId,
      investmentId,
      sellHoldingTrigger,
      recordDividendTrigger,
      recordWithholdingTaxTrigger,
      recordFeeTrigger,
      onTradeRecorded,
      isLinkedSettlement,
      settlementAccountName,
    ],
  );

  // Payload is built from the command's type, not one superset object with optional keys — forbidNonWhitelisted turns a mismatched field into a 400.
  // Omitting the charge where accepted is meaningful too: it removes the charge event the command recorded.
  const editTradeCommand = useCallback(
    async (
      values: TradeCorrectionFormValues,
      helpers: TradeCorrectionSubmitHelpers,
    ) => {
      // The panel is only ever opened with a command, but that guarantee lives on
      // the other side of a file boundary — narrow here instead of asserting.
      if (!correctedCommand) {
        helpers.setPanelError("This correction is no longer available.");
        return;
      }

      const isSettlement = isSettlementType(correctedCommand.type);
      const payload: EditTradeCommandPayload = {
        amountEur: Number(values.amountEur),
        effectiveDate: formatDate(values.date),
        // Quantity belongs to a buy and a sell only, and stays a string — a JSON
        // number cannot carry DECIMAL(28,6) and the DTO rejects one outright.
        ...(isSettlement
          ? { quantity: normalizeQuantity(values.quantity) }
          : {}),
        ...correctionChargeOf(correctedCommand, values),
        ...(acceptsCashback(correctedCommand)
          ? cashbackFundingPayloadOf(values)
          : {}),
        // The same predicate the panel gates the control on, so the field is sent
        // exactly when it was offered.
        ...(acceptsDirection(correctedCommand)
          ? { direction: values.direction }
          : {}),
        ...noteFields(values.note),
      };

      // Warns through the same channel as a fresh entry: moving a credit later,
      // or shrinking one, can leave the account short just the same.
      await submitLedgerCommand<TradeCorrectionFormValues>({
        promise: editTradeCommandTrigger({
          commandId: correctedCommand.commandId,
          investmentId,
          accountId,
          payload,
          movesTransactions:
            isLinkedSettlement ||
            correctedCommand.cashback !== null ||
            payload.cashbackEur !== undefined,
        }).unwrap(),
        successMessage: `${capitalizeFirstLetter(commandNoun(correctedCommand))} updated`,
        onRecorded: onCorrectionRecorded,
        helpers,
        mapError: mapTradeCorrectionError,
        settlementAccountName,
      });
    },
    [
      accountId,
      investmentId,
      correctedCommand,
      editTradeCommandTrigger,
      onCorrectionRecorded,
      isLinkedSettlement,
      settlementAccountName,
    ],
  );

  return { buyHolding, recordHoldingTrade, editTradeCommand };
};
